import { useEffect, useRef, useState } from "react";
import { Grid } from "@mui/material";
import { X } from "@phosphor-icons/react";
import axios, { AxiosError } from "axios";
import Swal from "sweetalert2";
import { AppliedItem, MainTitle, RaffleContent } from "../../../styles/theme";
import {
  CustomButtonDisabled,
  CustomButtonGray,
  CustomButtonGreen,
} from "../../../styles/button";
import { GREENCONTENTS, REDTITLE } from "../../../styles/color";
import {
  VehiclePhotoInfo,
  VehiclePhotoPhase,
} from "../../../system/types/Vehicle";
import { VehicleApi } from "../../../system/api/VehicleApi";
import { ErrorHandler } from "../../../system/ApiService";
import { useSasToken } from "../../../hooks/useSasToken";
import { useLoadingDispatch } from "../../../system/context/LoadingContext";
import AlertModal from "../../Common/AlertModal";
import VehiclePhotoUploader from "./VehiclePhotoUploader";
import {
  PHOTO_PHASE_LABEL,
  VehiclePhotoDraft,
  draftFromPhoto,
  removedSavedCount,
  revokeDrafts,
  uploadInterruptedMessage,
  uploadVehiclePhotos,
} from "./vehiclePhoto";

interface VehiclePhotoEditFormProps {
  reservationId: number;
  phase: VehiclePhotoPhase;
  /** 지금 저장돼 있는 해당 단계 사진 */
  savedPhotos: VehiclePhotoInfo[];
  /** 도착 사진을 고칠 때 같은 각도인지 비교하도록 출발 사진을 깔아준다 */
  referencePhotos?: VehiclePhotoInfo[];
  onSaved: () => void;
  onClose: () => void;
}

/**
 * 관리자 전용 차량 사진 수정.
 * 제출된 사진은 증빙이라 예약자는 바꿀 수 없고, 관리자가 받은 사진을 붙이거나 잘못 올린 사진을 바로잡는다.
 * 4장 필수 규칙은 적용하지 않는다 (대행 처리된 예약에 받은 사진만 붙이는 경우).
 */
function VehiclePhotoEditForm({
  reservationId,
  phase,
  savedPhotos,
  referencePhotos,
  onSaved,
  onClose,
}: VehiclePhotoEditFormProps) {
  const [drafts, setDrafts] = useState<VehiclePhotoDraft[]>(() =>
    savedPhotos.map(draftFromPhoto)
  );
  const [submitting, setSubmitting] = useState(false);
  const { refreshSasToken } = useSasToken();
  const Loading = useLoadingDispatch();

  // 모달이 닫히면 새로 고른 사진의 미리보기 URL 을 해제한다.
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;
  // 저장에 성공하면 onSaved 가 이 화면을 닫으므로, 그 뒤에는 상태를 건드리지 않는다.
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
      revokeDrafts(draftsRef.current);
    },
    []
  );

  const changed =
    drafts.some((draft) => !draft.uploaded) ||
    removedSavedCount(savedPhotos, drafts) > 0;

  const confirmRemoval = async (removed: number) => {
    if (removed === 0) return true;
    const result = await Swal.fire({
      title: "사진을 바꾸시겠습니까?",
      html: `기존 사진 ${removed}장이 목록에서 빠집니다.<br>빠진 사진은 화면에서 다시 볼 수 없습니다.`,
      icon: "question",
      confirmButtonText: "예",
      cancelButtonText: "아니오",
      showCancelButton: true,
      confirmButtonColor: GREENCONTENTS,
      cancelButtonColor: REDTITLE,
    });
    return result.isConfirmed;
  };

  const save = async () => {
    if (submitting || !changed) return;
    if (!(await confirmRemoval(removedSavedCount(savedPhotos, drafts)))) return;

    setSubmitting(true);
    let saved = false;
    // 새로 고른 사진 중 이미 올라간 장수 (기존 저장 사진은 세지 않는다)
    let uploadedCount = drafts.filter((draft) => draft.file && draft.uploaded).length;
    try {
      const needsUpload = drafts.some((draft) => !draft.uploaded);
      if (needsUpload) {
        Loading({ type: "LOADING_MESSAGE", message: "파일 업로드 중입니다." });
      }
      const uploaded = await uploadVehiclePhotos({
        drafts,
        reservationId,
        phase,
        getSasUrl: refreshSasToken,
        onProgress: (done, total) =>
          needsUpload &&
          Loading({
            type: "LOADING_MESSAGE",
            message: `파일 업로드 중입니다. (${done}/${total})`,
          }),
        // 한 장 올라갈 때마다 기억해 둔다. 중간에 끊겨도 다시 저장할 때 올라간 사진은 건너뛴다.
        onUploaded: (draft) => {
          uploadedCount += 1;
          if (!mountedRef.current) return;
          setDrafts((current) =>
            current.map((item) => (item.key === draft.key ? draft : item))
          );
        },
      });
      Loading({ type: "LOADING" });
      await VehicleApi.updateVehiclePhotos(
        reservationId,
        phase,
        uploaded.flatMap((draft) => (draft.uploaded ? [draft.uploaded] : []))
      );
      saved = true;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        AlertModal("msg", ErrorHandler(err as AxiosError));
      } else {
        console.error(err);
        AlertModal("msg", uploadInterruptedMessage(uploadedCount));
      }
    } finally {
      Loading({ type: "COMPLETE" });
      if (mountedRef.current) setSubmitting(false);
    }
    if (saved) {
      AlertModal("success", `${PHOTO_PHASE_LABEL[phase]}을 저장했습니다.`);
      onSaved();
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <RaffleContent>
          <MainTitle>{PHOTO_PHASE_LABEL[phase]} 수정</MainTitle>
          <AppliedItem onClick={onClose}>
            <X size={32} />
          </AppliedItem>
        </RaffleContent>
        <VehiclePhotoUploader
          phase={phase}
          drafts={drafts}
          onChange={setDrafts}
          referencePhotos={phase === "END" ? referencePhotos : undefined}
          required={false}
          preferGallery
        />
        <Grid container justifyContent="right" mt="16px">
          <CustomButtonGray type="button" onClick={onClose}>
            취소
          </CustomButtonGray>
          {submitting || !changed ? (
            <CustomButtonDisabled type="button">
              {submitting ? "저장 중" : "변경 없음"}
            </CustomButtonDisabled>
          ) : (
            <CustomButtonGreen type="button" onClick={save}>
              저장
            </CustomButtonGreen>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}

export default VehiclePhotoEditForm;
